/**
 * Text body widget (TBW) presentation resolvers.
 *
 * Both the palette and the alignment reach the canvas as a single free-form
 * string each, compiled from an Airtable single-select ("Style" -> `theme`,
 * "Text alignment" -> `textAlign`). Authors can rename those select options, and
 * scenes deployed years apart carry different spellings, so resolution is
 * substring-based and case-insensitive rather than an exact-match table: an
 * unrecognised value degrades to the default preset instead of rendering
 * unstyled.
 *
 * "borderless" is deliberately an independent axis from the palette so
 * "Borderless (Dark)" resolves to dark + borderless. The pre-preset
 * implementation tested `theme.includes("Dark")` before `includes("Borderless")`
 * in a single ternary chain, which gave that combination a white border.
 *
 * `customColor` is a third independent axis: it overrides the selected preset's
 * fill (and, by contrast, its text colour), while the border and drop shadow
 * stay whatever the preset says. There is deliberately no "Custom" option in the
 * Style field — an author picks the preset whose border they want and then
 * recolours its fill.
 */

import chroma from "chroma-js";

import className from "../style.module.scss";

/** WCAG 2.2 AA minimum for body-size text. */
const AA_BODY_TEXT = 4.5;

// Ordered by preference, not by contrast. White comes first so a dark custom
// fill keeps white text; charcoal — the colour the Light (default) preset already
// uses — comes next so a pale custom fill still reads like the rest of the
// presets; near-black is the escalation for fills charcoal cannot clear. Each
// entry carries the blur colour the small state smears, which needs a fully
// saturated black/white rather than the softer body colour.
const TEXT_LADDER = [
  { text: "#fff", blur: "#fff" },
  { text: "#333", blur: "#000" },
  { text: "#111", blur: "#000" },
];

const PALETTE_CLASSES = {
  light: "",
  dark: className.paletteDark,
  orange: className.paletteOrange,
  red: className.paletteRed,
};

// [resolved mode, keywords that select it], in match order. Kept as data rather
// than a ternary chain so the precedence is readable and a keyword alias is one
// array entry: a value naming two modes is malformed, and first-match makes which
// one wins deterministic instead of arbitrary.
const PALETTE_KEYWORDS = [
  ["dark", ["dark"]],
  ["orange", ["orange"]],
  ["red", ["red"]],
];

const ALIGNMENT_KEYWORDS = [
  ["auto", ["auto"]],
  ["center", ["center", "centre"]],
  ["right", ["right"]],
  ["left", ["left"]],
];

/**
 * First mode in `table` whose keywords appear in `key`, else `fallback`.
 *
 * @param {string} key lowercased scene value
 * @param {Array<[string, string[]]>} table
 * @param {string} fallback
 * @returns {string}
 */
const matchKeyword = (key, table, fallback) =>
  table.find(([, keywords]) =>
    keywords.some((word) => key.includes(word))
  )?.[0] ?? fallback;

// `left` is the bare `.textBody` (which stretches to the wrapper and is
// text-align: left); `auto` opts back out of that stretch so the block
// shrink-wraps and the wrapper's `align-items: center` centres it.
const ALIGNMENT_CLASSES = {
  auto: className.alignAuto,
  left: "",
  center: className.alignCenter,
  right: className.alignRight,
};

// The expanded widget is a plain block that always fills the modal, so there is
// no shrink-wrap for `auto` to centre — it renders left, like `left`.
const WIDGET_ALIGNMENT_CLASSES = {
  auto: "",
  left: "",
  center: className.alignCenter,
  right: className.alignRight,
};

/**
 * @param {string} [theme] raw scene value, e.g. "Borderless (Dark)"
 * @returns {{palette: string, borderless: boolean}}
 */
export const resolveTextBodyTheme = (theme) => {
  const key = String(theme ?? "").toLowerCase();

  // "bordered" contains "red", so the border token is dropped before matching a
  // palette. Without this a renamed option like "Bordered (Light)" would resolve
  // to the red palette — the same class of fault as the Dark-before-Borderless
  // ordering bug described above. Borderless is detected from the untouched key.
  const paletteKey = key.replace(/border\w*/g, "");

  return {
    palette: matchKeyword(paletteKey, PALETTE_KEYWORDS, "light"),
    borderless: key.includes("borderless"),
  };
};

/**
 * Four modes, not three. An empty field and "Auto (default)" both resolve to
 * `auto` — text is centred while it fits on one line and left-aligned once it
 * wraps — because that is how text bodies have always rendered. Keeping it the
 * default means adding this field changes nothing about existing scenes. `left`
 * is the opt-in for literal left alignment at any length.
 *
 * "auto" is tested first so a descriptive rename like "Auto (centre short text)"
 * still resolves to `auto` rather than to `center`.
 *
 * @param {string} [textAlign] raw scene value, e.g. "Auto (default)"
 * @returns {"auto" | "left" | "center" | "right"}
 */
export const resolveTextBodyAlignment = (textAlign) =>
  matchKeyword(
    String(textAlign ?? "").toLowerCase(),
    ALIGNMENT_KEYWORDS,
    "auto"
  );

/**
 * Wrapper modifier classes for a raw scene `theme` value. `light` and the
 * bordered default contribute nothing — they are the base `.textBodyWrapper`.
 *
 * @param {string} [theme]
 * @returns {string[]}
 */
export const textBodyWrapperClasses = (theme) => {
  const { palette, borderless } = resolveTextBodyTheme(theme);

  return [
    PALETTE_CLASSES[palette],
    borderless ? className.borderless : "",
  ].filter(Boolean);
};

/**
 * The alignment modifier for the in-canvas text body.
 *
 * @param {string} [textAlign]
 * @returns {string}
 */
export const textBodyAlignmentClass = (textAlign) =>
  ALIGNMENT_CLASSES[resolveTextBodyAlignment(textAlign)];

/**
 * The alignment modifier for the expanded text-body widget (WidgetWrapper). The
 * explicit alignments share their class names with `.textBody`; `auto` has no
 * equivalent there, because that block always fills the modal.
 *
 * @param {string} [textAlign]
 * @returns {string}
 */
export const textBodyWidgetAlignmentClass = (textAlign) =>
  WIDGET_ALIGNMENT_CLASSES[resolveTextBodyAlignment(textAlign)];

/**
 * Resolve a raw scene `customColor` into a fill plus the contrast-safe text and
 * blur colours to pair with it. Returns null when the field is absent or not a
 * colour, so the selected preset renders untouched.
 *
 * Alpha is dropped: a translucent fill lets the canvas artwork show through,
 * which would make the measured contrast meaningless.
 *
 * Inline custom properties built from this beat the values the palette classes
 * set, so only the fill and text change — `--tbw-border` and the drop shadow keep
 * coming from the selected preset.
 *
 * The value is trimmed first: it comes from a free-text Airtable field, and
 * chroma rejects an otherwise valid colour that carries surrounding whitespace
 * (a pasted " #2d1b4e" would be dropped silently). A bare hex without the leading
 * "#", and any casing, chroma already accepts.
 *
 * @param {string} [customColor] any CSS colour chroma accepts; hex in practice
 * @returns {{background: string, text: string, blur: string, contrast: number, meetsAA: boolean} | null}
 */
export const resolveTextBodyCustomColor = (customColor) => {
  if (typeof customColor !== "string") {
    return null;
  }

  const value = customColor.trim();

  if (!value || !chroma.valid(value)) {
    return null;
  }

  const background = chroma(value).hex("rgb");
  const ratio = (candidate) => chroma.contrast(background, candidate.text);

  const passing = TEXT_LADDER.find((c) => ratio(c) >= AA_BODY_TEXT);
  // A mid-tone fill can leave every option short of 4.5:1 — no text colour can
  // rescue it. Use the strongest of the three rather than something arbitrary,
  // and report it so callers can surface the shortfall.
  const chosen =
    passing ??
    TEXT_LADDER.reduce((best, c) => (ratio(c) > ratio(best) ? c : best));

  return {
    background,
    text: chosen.text,
    blur: chosen.blur,
    contrast: Number(ratio(chosen).toFixed(2)),
    meetsAA: Boolean(passing),
  };
};
